import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CreditCard,
  Plus,
  Eye,
  Receipt,
  CheckCircle,
  AlertTriangle,
  Edit,
  Trash2,
  Lock,
  Unlock,
  Building2,
  DollarSign,
  TrendingUp,
  ShieldCheck,
  Calendar,
  UserCheck,
  RefreshCw,
  FileText,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const INITIAL_CARD_FORM = {
  card_name: '',
  bank_name: 'HDFC Bank',
  card_type: 'Visa',
  card_number: '',
  cardholder_name: '',
  company_name: '',
  department: 'Finance',
  assigned_team: 'Finance & Travel Desk',
  assigned_finance_user: '',
  cost_center: '',
  business_unit: '',
  credit_limit: 500000,
  available_limit: 500000,
  currency: 'INR',
  interest_rate: 0,
  annual_fee: 0,
  reward_program: 'Corporate Travel Rewards',
  issue_date: new Date().toISOString().split('T')[0],
  expiry_month_year: '12/28',
  billing_cycle_day: 1,
  statement_date_day: 2,
  due_date_day: 15,
  status: 'active',
  remarks: '',
};

const TravelCreditCards = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [creditCards, setCreditCards] = useState([]);
  const [summary, setSummary] = useState(null);

  // Modals state
  const [showCardModal, setShowCardModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [cardForm, setCardForm] = useState(INITIAL_CARD_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [statementCard, setStatementCard] = useState(null);
  const [deleteConfirmCard, setDeleteConfirmCard] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [cardsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/travel-desk/credit-cards`, { withCredentials: true }),
        axios.get(`${API_URL}/travel-desk/credit-cards/finance-summary`, { withCredentials: true }),
      ]);
      setCreditCards(cardsRes.data.credit_cards || []);
      setSummary(summaryRes.data.summary || null);
    } catch (err) {
      toast.error('Failed to load corporate credit cards data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingCard(null);
    setCardForm(INITIAL_CARD_FORM);
    setShowCardModal(true);
  };

  const handleOpenEditModal = (card) => {
    setEditingCard(card);
    setCardForm({
      card_name: card.card_name || '',
      bank_name: card.bank_name || 'HDFC Bank',
      card_type: card.card_type || 'Visa',
      card_number: card.card_number_masked || '',
      cardholder_name: card.cardholder_name || '',
      company_name: card.company_name || '',
      department: card.department || 'Finance',
      assigned_team: card.assigned_team || '',
      assigned_finance_user: card.assigned_finance_user || '',
      cost_center: card.cost_center || '',
      business_unit: card.business_unit || '',
      credit_limit: card.credit_limit || 500000,
      available_limit: card.available_limit !== undefined ? card.available_limit : card.credit_limit || 500000,
      currency: card.currency || 'INR',
      interest_rate: card.interest_rate || 0,
      annual_fee: card.annual_fee || 0,
      reward_program: card.reward_program || '',
      issue_date: card.issue_date || new Date().toISOString().split('T')[0],
      expiry_month_year: card.expiry_month_year || '12/28',
      billing_cycle_day: card.billing_cycle_day || 1,
      statement_date_day: card.statement_date_day || 2,
      due_date_day: card.due_date_day || 15,
      status: card.status || 'active',
      remarks: card.remarks || '',
    });
    setShowCardModal(true);
  };

  const handleSaveCard = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      if (editingCard) {
        await axios.put(`${API_URL}/travel-desk/credit-cards/${editingCard._id}`, cardForm, { withCredentials: true });
        toast.success('Corporate credit card updated successfully!');
      } else {
        await axios.post(`${API_URL}/travel-desk/credit-cards`, cardForm, { withCredentials: true });
        toast.success('Corporate credit card registered successfully!');
      }
      setShowCardModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save credit card');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (card, newStatus) => {
    try {
      await axios.patch(`${API_URL}/travel-desk/credit-cards/${card._id}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Card status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update card status');
    }
  };

  const handleDeleteCard = async (cardId) => {
    try {
      await axios.delete(`${API_URL}/travel-desk/credit-cards/${cardId}`, { withCredentials: true });
      toast.success('Corporate credit card deleted');
      setDeleteConfirmCard(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete card');
    }
  };

  const handleOpenStatement = async (cardId) => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/credit-cards/${cardId}/statement`, {
        withCredentials: true,
      });
      setStatementCard(data.statement);
    } catch (err) {
      toast.error('Failed to load card statement');
    }
  };

  const canEditCard = hasPermission(user, 'travel_desk.credit_cards.edit');
  const canDeleteCard = hasPermission(user, 'travel_desk.credit_cards.delete');

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <CreditCard className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Company Corporate Credit Cards</h1>
            <p className="text-xs text-blue-200">
              Database Master · Limits, Billing Cycles, Department Allocation &amp; Monthly Statements.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {canEditCard && (
            <button
              onClick={handleOpenCreateModal}
              className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-lg flex items-center gap-2 transition-all transform hover:-translate-y-0.5"
            >
              <Plus className="w-4 h-4" /> Create Corporate Credit Card
            </button>
          )}
        </div>
      </div>

      {/* Finance Summary Bar */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-1">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Total Credit Limit</div>
            <div className="text-xl font-black text-slate-900 font-mono">
              ₹ {(summary.total_credit_limit || 0).toLocaleString('en-IN')}
            </div>
            <div className="text-[10px] text-slate-400 font-medium">Combined master ceiling</div>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-1">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Available Limit</div>
            <div className="text-xl font-black text-emerald-600 font-mono">
              ₹ {(summary.total_available_limit || 0).toLocaleString('en-IN')}
            </div>
            <div className="text-[10px] text-emerald-600 font-bold">Ready for travel bookings</div>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-1">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Utilized Amount</div>
            <div className="text-xl font-black text-amber-600 font-mono">
              ₹ {(summary.total_utilized_amount || 0).toLocaleString('en-IN')}
            </div>
            <div className="text-[10px] text-amber-600 font-bold">Billed across tickets</div>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-1">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Active Cards</div>
            <div className="text-xl font-black text-blue-600 font-mono">
              {summary.active_cards} / {summary.total_cards}
            </div>
            <div className="text-[10px] text-slate-400 font-medium">Assigned to desks</div>
          </div>
        </div>
      )}

      {/* Main Grid / Empty State */}
      {loading ? (
        <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center text-slate-500 font-medium">
          Loading company credit cards...
        </div>
      ) : creditCards.length === 0 ? (
        /* Empty State */
        <div className="bg-white rounded-2xl border-2 border-dashed border-slate-300 p-12 text-center space-y-4 shadow-sm">
          <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto text-amber-500 border border-amber-200">
            <CreditCard className="w-8 h-8" />
          </div>
          <div className="space-y-1">
            <h3 className="text-lg font-black text-slate-900">No Company Credit Cards Found</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              No corporate credit cards have been registered in the database yet. Click below to add the first company card to the master.
            </p>
          </div>
          {canEditCard && (
            <button
              onClick={handleOpenCreateModal}
              className="px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-md inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Create Corporate Credit Card
            </button>
          )}
        </div>
      ) : (
        /* Card Cards Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {creditCards.map((card) => {
            const limit = card.credit_limit || 1;
            const avail = card.available_limit !== undefined ? card.available_limit : limit;
            const util = card.utilized_amount !== undefined ? card.utilized_amount : Math.max(0, limit - avail);
            const utilPct = Math.min(100, Math.round((util / limit) * 100));

            return (
              <div
                key={card._id}
                className="bg-gradient-to-br from-slate-900 via-slate-950 to-indigo-950 text-white rounded-2xl p-6 shadow-xl space-y-4 border border-slate-800 flex flex-col justify-between"
              >
                <div className="space-y-4">
                  {/* Top Bar */}
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-[10px] font-extrabold tracking-widest text-amber-400 uppercase">
                        {card.bank_name || 'HDFC Bank'}
                      </span>
                      <h3 className="font-bold text-sm text-white">{card.card_name}</h3>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                      card.status === 'active'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        : card.status === 'blocked'
                        ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                        : 'bg-slate-700 text-slate-300'
                    }`}>
                      {card.status}
                    </span>
                  </div>

                  {/* Masked Card Number */}
                  <div className="font-mono text-lg tracking-widest text-slate-200 bg-slate-900/60 p-3 rounded-xl border border-slate-800 flex justify-between items-center">
                    <span>{card.card_number_masked}</span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">{card.card_type || 'Visa'}</span>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-2 text-xs border-t border-slate-800/80 pt-3 text-slate-300">
                    <div>
                      <div className="text-slate-500 text-[10px] font-bold uppercase">Cardholder</div>
                      <div className="font-bold truncate text-slate-100">{card.cardholder_name}</div>
                    </div>
                    <div>
                      <div className="text-slate-500 text-[10px] font-bold uppercase">Dept / Team</div>
                      <div className="font-bold truncate text-slate-100">{card.department} · {card.assigned_team || 'Desk'}</div>
                    </div>
                    <div>
                      <div className="text-slate-500 text-[10px] font-bold uppercase">Expiry</div>
                      <div className="font-mono font-bold text-slate-200">{card.expiry_month_year}</div>
                    </div>
                    <div>
                      <div className="text-slate-500 text-[10px] font-bold uppercase">Statement Day</div>
                      <div className="font-bold text-slate-200">Day {card.statement_date_day || 2} of Month</div>
                    </div>
                  </div>

                  {/* Limit Progress Bar */}
                  <div className="space-y-1.5 border-t border-slate-800/80 pt-3">
                    <div className="flex justify-between text-xs font-bold">
                      <span className="text-slate-400">Available: <b className="text-emerald-400 font-mono">₹ {(avail || 0).toLocaleString('en-IN')}</b></span>
                      <span className="text-slate-400">Limit: <b className="text-slate-200 font-mono">₹ {(limit || 0).toLocaleString('en-IN')}</b></span>
                    </div>
                    <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${utilPct > 80 ? 'bg-rose-500' : utilPct > 50 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                        style={{ width: `${utilPct}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-slate-400 text-right">
                      Utilized: ₹ {(util || 0).toLocaleString('en-IN')} ({utilPct}%)
                    </div>
                  </div>
                </div>

                {/* Actions Footer */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-slate-800/80">
                  <button
                    onClick={() => handleOpenStatement(card._id)}
                    className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-lg text-xs shadow-md flex items-center gap-1"
                  >
                    <FileText className="w-3.5 h-3.5" /> View Statement
                  </button>

                  <div className="flex items-center gap-1">
                    {canEditCard && (
                      <button
                        onClick={() => handleOpenEditModal(card)}
                        className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs"
                        title="Edit Card"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {canEditCard && (
                      <button
                        onClick={() => handleToggleStatus(card, card.status === 'active' ? 'blocked' : 'active')}
                        className={`p-1.5 rounded-lg text-xs ${
                          card.status === 'active'
                            ? 'bg-rose-950 text-rose-300 hover:bg-rose-900'
                            : 'bg-emerald-950 text-emerald-300 hover:bg-emerald-900'
                        }`}
                        title={card.status === 'active' ? 'Block Card' : 'Activate Card'}
                      >
                        {card.status === 'active' ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                      </button>
                    )}

                    {canDeleteCard && (
                      <button
                        onClick={() => setDeleteConfirmCard(card)}
                        className="p-1.5 bg-rose-900/40 text-rose-300 hover:bg-rose-900 rounded-lg text-xs"
                        title="Delete Card"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Card Modal */}
      {showCardModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-amber-500" />
                {editingCard ? 'Edit Corporate Credit Card' : 'Create Corporate Credit Card Master'}
              </h3>
              <button onClick={() => setShowCardModal(false)} className="text-slate-400 hover:text-slate-600 font-bold">
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveCard} className="space-y-4 text-xs">
              {/* Section 1: Card Identification */}
              <div className="space-y-2">
                <h4 className="font-bold text-slate-800 uppercase tracking-wider text-[10px] text-blue-600">1. Card Identification</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Card Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. HDFC Platinum Corporate Card"
                      value={cardForm.card_name}
                      onChange={(e) => setCardForm({ ...cardForm, card_name: e.target.value })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl font-semibold outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Bank Name *</label>
                    <select
                      value={cardForm.bank_name}
                      onChange={(e) => setCardForm({ ...cardForm, bank_name: e.target.value })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl font-semibold outline-none"
                    >
                      <option value="HDFC Bank">HDFC Bank</option>
                      <option value="ICICI Bank">ICICI Bank</option>
                      <option value="Axis Bank">Axis Bank</option>
                      <option value="State Bank of India">State Bank of India</option>
                      <option value="Kotak Mahindra Bank">Kotak Mahindra Bank</option>
                      <option value="Standard Chartered">Standard Chartered</option>
                      <option value="American Express">American Express</option>
                      <option value="HSBC Bank">HSBC Bank</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Card Network Type</label>
                    <select
                      value={cardForm.card_type}
                      onChange={(e) => setCardForm({ ...cardForm, card_type: e.target.value })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl font-semibold outline-none"
                    >
                      <option value="Visa">Visa</option>
                      <option value="Mastercard">Mastercard</option>
                      <option value="American Express">American Express</option>
                      <option value="RuPay">RuPay</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Card Number (Last 4 digits or Full) *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 4892 or 4532 1234 5678 4892"
                      value={cardForm.card_number}
                      onChange={(e) => setCardForm({ ...cardForm, card_number: e.target.value })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Ownership & Assignment */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <h4 className="font-bold text-slate-800 uppercase tracking-wider text-[10px] text-blue-600">2. Ownership &amp; Desk Assignment</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Cardholder Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Finance Desk - Travel Team"
                      value={cardForm.cardholder_name}
                      onChange={(e) => setCardForm({ ...cardForm, cardholder_name: e.target.value })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl font-semibold outline-none"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Department</label>
                    <input
                      type="text"
                      placeholder="Finance / Admin"
                      value={cardForm.department}
                      onChange={(e) => setCardForm({ ...cardForm, department: e.target.value })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl font-semibold outline-none"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Assigned Desk / Team</label>
                    <input
                      type="text"
                      placeholder="e.g. Travel Booking Team"
                      value={cardForm.assigned_team}
                      onChange={(e) => setCardForm({ ...cardForm, assigned_team: e.target.value })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Cost Center / BU</label>
                    <input
                      type="text"
                      placeholder="e.g. CC-102 Corporate"
                      value={cardForm.cost_center}
                      onChange={(e) => setCardForm({ ...cardForm, cost_center: e.target.value })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Financial Limits */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <h4 className="font-bold text-slate-800 uppercase tracking-wider text-[10px] text-blue-600">3. Limits &amp; Billing Cycle</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Credit Limit (₹) *</label>
                    <input
                      type="number"
                      required
                      value={cardForm.credit_limit}
                      onChange={(e) => setCardForm({ ...cardForm, credit_limit: Number(e.target.value) })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold outline-none"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Available Limit (₹)</label>
                    <input
                      type="number"
                      value={cardForm.available_limit}
                      onChange={(e) => setCardForm({ ...cardForm, available_limit: Number(e.target.value) })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold outline-none text-emerald-700"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Expiry (MM/YY)</label>
                    <input
                      type="text"
                      placeholder="12/28"
                      value={cardForm.expiry_month_year}
                      onChange={(e) => setCardForm({ ...cardForm, expiry_month_year: e.target.value })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl font-mono outline-none"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Billing Cycle Day</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={cardForm.billing_cycle_day}
                      onChange={(e) => setCardForm({ ...cardForm, billing_cycle_day: Number(e.target.value) })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Statement Date Day</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={cardForm.statement_date_day}
                      onChange={(e) => setCardForm({ ...cardForm, statement_date_day: Number(e.target.value) })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Payment Due Day</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={cardForm.due_date_day}
                      onChange={(e) => setCardForm({ ...cardForm, due_date_day: Number(e.target.value) })}
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Buttons */}
              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCardModal(false)}
                  className="px-4 py-2 bg-slate-200 font-bold rounded-xl text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold rounded-xl shadow-md"
                >
                  {submitting ? 'Saving...' : editingCard ? 'Update Credit Card' : 'Save Credit Card Master'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Statement Modal */}
      {statementCard && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base">
                  Corporate Card Monthly Statement ({statementCard.statement_month})
                </h3>
                <p className="text-xs text-slate-500">{statementCard.card_name} · {statementCard.card_number_masked}</p>
              </div>
              <button onClick={() => setStatementCard(null)} className="text-slate-400 hover:text-slate-600 font-bold text-lg">
                &times;
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-3 gap-3 bg-slate-50 p-3.5 rounded-xl font-bold border border-slate-200">
                <div>
                  <span className="text-slate-400 block text-[10px]">TOTAL SPENT</span>
                  <span className="text-emerald-700 font-mono text-sm">₹ {(statementCard.total_bookings_amount || 0).toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">PAYMENT DUE DATE</span>
                  <span className="text-slate-900 font-mono text-sm">{statementCard.due_date || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">STATUS</span>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] uppercase">{statementCard.payment_status}</span>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-800 mb-2">Linked Travel Bookings &amp; Transactions</h4>
                <div className="overflow-x-auto border border-slate-200 rounded-xl">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 font-bold uppercase text-[10px] text-slate-700 border-b border-slate-200">
                      <tr>
                        <th className="p-2.5 text-left">Booking Ref</th>
                        <th className="p-2.5 text-left">Req #</th>
                        <th className="p-2.5 text-left">Employee</th>
                        <th className="p-2.5 text-left">Vendor</th>
                        <th className="p-2.5 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                      {(statementCard.linked_bookings || []).length === 0 ? (
                        <tr>
                          <td colSpan="5" className="p-4 text-center text-slate-400 italic">No bookings recorded on this card yet.</td>
                        </tr>
                      ) : (
                        statementCard.linked_bookings.map((b) => (
                          <tr key={b._id} className="hover:bg-slate-50">
                            <td className="p-2.5 font-mono font-bold text-blue-600">{b.booking_number}</td>
                            <td className="p-2.5 font-mono">{b.request_number || 'N/A'}</td>
                            <td className="p-2.5 font-bold">{b.employee_name}</td>
                            <td className="p-2.5">{b.vendor_name}</td>
                            <td className="p-2.5 font-mono font-bold text-right text-emerald-700">₹ {(b.total_amount || 0).toLocaleString('en-IN')}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmCard && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3 text-rose-600 font-bold text-base border-b border-slate-200 pb-3">
              <AlertTriangle className="w-6 h-6" /> Confirm Delete Credit Card
            </div>
            <p className="text-xs text-slate-600">
              Are you sure you want to delete corporate credit card <b className="text-slate-900">{deleteConfirmCard.card_name}</b> ({deleteConfirmCard.card_number_masked})? This action will remove the card master document from the database.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 text-xs">
              <button onClick={() => setDeleteConfirmCard(null)} className="px-4 py-2 bg-slate-100 font-bold rounded-xl">
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCard(deleteConfirmCard._id)}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white font-bold rounded-xl shadow-md"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelCreditCards;
